import React from 'react';
import { motion } from 'framer-motion';
import DownloadAppButton from '../DownloadAppButton';

// Import des images pour les étapes
import step1Img from '../../assets/images/step1.jpg';
import step2Img from '../../assets/images/step2.jpg';
import step3Img from '../../assets/images/step3.jpg';
// Import de l'aperçu de l'app mobile
import appPreviewImg from '../../assets/images/app-preview.png';

const steps = [
  {
    number: '01',
    title: "Téléchargez l'application",
    description: "Téléchargez l'APK directement depuis le site. Installation en moins d'une minute.",
    image: step1Img,
  },
  {
    number: '02',
    title: 'Trouvez votre prestataire',
    description: 'Parcourez les catégories, consultez les profils vérifiés et les avis clients, puis réservez.',
    image: step2Img,
  },
  {
    number: '03',
    title: 'Profitez du service',
    description: "Payez en toute sécurité via l'app et évaluez votre expérience pour aider la communauté.",
    image: step3Img,
  },
];

const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 bg-[#111111]">
      <div className="container-custom">
        <h2 className="section-title">
          Comment ça <span className="text-[#F97316]">marche</span>
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="text-center card group"
            >
              {/* Container image - même taille que l'ancien cercle d'icône */}
              <div className="w-20 h-20 rounded-full overflow-hidden mx-auto mb-6 shadow-vadom ring-2 ring-[#F97316]/30 group-hover:ring-[#F97316] transition-all duration-300">
                <img 
                  src={step.image}
                  alt={step.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
              </div>
              <div className="text-[#F97316] font-bold text-sm mb-2">Étape {step.number}</div>
              <h3 className="text-xl font-semibold mb-3 text-white">{step.title}</h3>
              <p className="text-[#AAAAAA]">{step.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Section téléchargement de l'application - Version stylisée native */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-20 relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#1a1a1a] via-[#0f0f0f] to-[#0a0a0a] border border-[#2a2a2a] shadow-2xl"
        >
          <div className="absolute top-0 right-0 w-96 h-96 bg-[#F97316]/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-[#F97316]/5 rounded-full blur-3xl" />
          
          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between p-8 lg:p-12 gap-8">
            {/* Partie gauche - Texte et boutons */}
            <div className="flex-1 text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
              >
                <div className="inline-flex items-center gap-2 bg-[#F97316]/10 px-4 py-2 rounded-full mb-6">
                  <span className="text-[#F97316] text-2xl">📱</span>
                  <span className="text-[#F97316] text-sm font-medium">Application mobile</span>
                </div>
                
                <h3 className="text-3xl lg:text-4xl font-display font-bold mb-4 text-white">
                  Téléchargez <span className="text-[#F97316]">VADOM</span>
                </h3>
                
                <p className="text-[#AAAAAA] text-lg mb-8 max-w-md mx-auto lg:mx-0">
                  Pour une expérience optimale, utilisez notre application mobile. 
                  Retrouvez tous vos services à portée de main.
                </p>
                
                {/* L'application n'est pas encore publiée sur les stores :
                    on sert l'APK directement */}
                <div className="flex flex-col sm:flex-row justify-center lg:justify-start gap-4">
                  <DownloadAppButton
                    variant="card"
                    className="group relative flex items-center gap-3 bg-[#1a1a1a] hover:bg-[#252525] border border-[#333333] rounded-xl px-5 py-3 transition-all duration-300"
                  />
                </div>

                <p className="text-xs text-gray-500 mt-4 text-center lg:text-left">
                  Bientôt sur Google Play et l'App Store. En attendant, autorisez
                  l'installation depuis votre navigateur à l'ouverture du fichier.
                </p>

                {/* Bénéfices supplémentaires */}
                <div className="flex flex-wrap justify-center lg:justify-start gap-4 mt-8">
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span className="text-green-500">✓</span> Paiement sécurisé
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span className="text-green-500">✓</span> Support 24/7
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-400">
                    <span className="text-green-500">✓</span> Services à la demande
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Partie droite - Aperçu de l'app mobile avec animation */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8, rotateY: 0 }}
              whileInView={{ opacity: 1, scale: 1, rotateY: 0 }}
              transition={{ delay: 0.3, duration: 0.6, type: "spring", stiffness: 100 }}
              className="flex-1 flex justify-center relative"
            >
              <motion.div
                animate={{
                  y: [0, -10, 0, 10, 0],
                  rotate: [0, 1, 0, -1, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="relative"
              >
                {/* Effet de lueur autour de l'image */}
                <div className="absolute -inset-4 bg-gradient-to-r from-[#F97316]/20 via-[#F97316]/10 to-transparent rounded-3xl blur-xl animate-pulse" />
                
                {/* Image de l'app */}
                <div className="relative rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10">
                  <img 
                    src={appPreviewImg}
                    alt="Aperçu de l'application VADOM"
                    className="w-64 md:w-80 h-auto object-contain"
                  />
                </div>

                {/* Badge flottant */}
                <motion.div
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.7, 1, 0.7]
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  className="absolute -top-4 -right-4 bg-[#F97316] text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg"
                >
                  ✨ Nouveau
                </motion.div>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorks;